import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
const control =
  "mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white px-4";
const empty = {
  date: "",
  status: "DISPUTED",
  goods: "",
  reason: "",
  evidenceReference: "",
};
export default function ReturnDisputes({ company, returnId, onChanged }) {
  const { accessToken, user } = useAuth(),
    canManage = hasPermission(user, "accounting.vouchers_post");
  const [view, setView] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [verified, setVerified] = useState(false),
    [form, setForm] = useState(empty);
  const generation = useRef(0),
    requestKey = useRef(null);
  useEffect(() => {
    const requests = generation;
    requests.current++;
    setView(null);
    setBusy(false);
    setError("");
    setVerified(false);
    requestKey.current = null;
    setForm(empty);
    return () => {
      requests.current++;
    };
  }, [company, returnId, accessToken]);
  async function load() {
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    try {
      const v = await apiRequest(
        `/accounting/${company}/supplier-returns/${returnId}/disputes`,
        { accessToken },
      );
      if (seq === generation.current) {
        setView(v);
        requestKey.current = null;
        setVerified(false);
      }
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  function change(key, value) {
    setForm((f) => ({
      ...f,
      [key]: value,
      ...(key === "status"
        ? { goods: value === "RESOLVED" ? "0.00" : "" }
        : {}),
    }));
    setVerified(false);
    requestKey.current = null;
  }
  async function save(e) {
    e.preventDefault();
    if (busy || !view || !canManage || !verified) return;
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    try {
      if (!window.crypto?.randomUUID)
        throw new Error(
          "Secure browser UUID support is required. Open this page on HTTPS or localhost.",
        );
      requestKey.current ||= window.crypto.randomUUID();
      const result = await apiRequest(
        `/accounting/${company}/supplier-returns/${returnId}/disputes`,
        {
          method: "POST",
          accessToken,
          body: {
            ...form,
            requestKey: requestKey.current,
            expectedEventId: view.latestEventId,
            expectedRemainingGoods: view.remainingGoods,
            decisionVerified: true,
          },
        },
      );
      if (seq !== generation.current) return;
      requestKey.current = null;
      setVerified(false);
      if (onChanged) {
        onChanged(result);
        return;
      }
      await load();
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  return (
    <details className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6">
      <summary className="cursor-pointer text-xl font-semibold">
        Supplier dispute / rejection tracking
      </summary>
      <p className="text-sm text-gray-600">
        Rejected goods remain in clearing, not an expense. Resolution releases
        the hold for separate credit review—it does not post a credit, reverse
        GST, move stock, allocate bills or record cash.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={load}
        className="h-11 rounded-xl border px-5 disabled:opacity-50"
      >
        Load Decision History
      </button>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {view && (
        <>
          <p className="text-sm font-medium">
            Awaiting goods credit ₹{view.remainingGoods} · Disputed/rejected ₹
            {view.disputedGoods} · Available for credit review ₹
            {view.availableCreditGoods}
          </p>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Decision date</th>
                  <th>Decision</th>
                  <th>Held goods</th>
                  <th>Reason</th>
                  <th>Evidence reference</th>
                </tr>
              </thead>
              <tbody>
                {view.history.map((h) => (
                  <tr key={h.id}>
                    <td>{h.date.slice(0, 10)}</td>
                    <td>{h.status}</td>
                    <td>₹{h.goods}</td>
                    <td>{h.reason}</td>
                    <td>{h.evidenceReference}</td>
                  </tr>
                ))}
                {!view.history.length && (
                  <tr>
                    <td colSpan={5}>No supplier decisions recorded.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {canManage && (
            <form onSubmit={save} className="space-y-4">
              <fieldset disabled={busy} className="grid gap-4 md:grid-cols-2">
                <label>
                  Decision date
                  <input
                    required
                    type="date"
                    className={control}
                    value={form.date}
                    onChange={(e) => change("date", e.target.value)}
                  />
                </label>
                <label>
                  Supplier decision
                  <select
                    className={control}
                    value={form.status}
                    onChange={(e) => change("status", e.target.value)}
                  >
                    <option value="DISPUTED">
                      Disputed — hold credit acceptance
                    </option>
                    <option value="REJECTED">
                      Supplier rejected credit — retain clearing
                    </option>
                    <option
                      value="RESOLVED"
                      disabled={
                        !view.latestEventId ||
                        view.history[0]?.status === "RESOLVED"
                      }
                    >
                      Resolved — release hold only
                    </option>
                  </select>
                </label>
                <label>
                  Goods amount on hold
                  <input
                    required
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]{1,2})?"
                    readOnly={form.status === "RESOLVED"}
                    className={control}
                    value={form.goods}
                    onChange={(e) => change("goods", e.target.value)}
                  />
                </label>
                <label>
                  Evidence / supplier reference
                  <input
                    required
                    minLength={3}
                    maxLength={150}
                    className={control}
                    value={form.evidenceReference}
                    onChange={(e) =>
                      change("evidenceReference", e.target.value)
                    }
                  />
                </label>
                <label className="md:col-span-2">
                  Decision reason
                  <textarea
                    required
                    minLength={10}
                    maxLength={1000}
                    className="mt-2 min-h-24 w-full rounded-xl border p-3"
                    value={form.reason}
                    onChange={(e) => change("reason", e.target.value)}
                  />
                </label>
              </fieldset>
              <p className="text-xs text-gray-500">
                Goods amount is the total current hold, not an additional
                amount. Retain referenced evidence separately; this form does
                not upload attachments.
              </p>
              <label className="flex gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={verified}
                  disabled={busy}
                  onChange={(e) => setVerified(e.target.checked)}
                />
                I verified the supplier decision and evidence; no financial
                balances or tax are adjusted.
              </label>
              <button
                disabled={busy || !verified}
                className="h-11 rounded-xl bg-primary-700 px-5 font-medium text-white disabled:opacity-50"
              >
                Record Supplier Decision
              </button>
            </form>
          )}
        </>
      )}
    </details>
  );
}
